import React, { Fragment, useEffect, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { requestUpdate, requestConfirm } from 'api/board/rawMaterial';
import { requestList as requestCode } from 'api/standard/code';
import { requestList } from 'api/standard/code/rawMaterial';
import Flex from 'components/common/Flex';
import Search from 'components/common-sfims/form/Search';
import Select from 'components/common-sfims/form/Select';
import List from 'components/common-sfims/List';
import Modal from 'components/common-sfims/modal/Common';
import { array2ValueAndText } from 'function/convert';
import { onEnterKeyPress, onInputChange } from 'function/event';
import { getText } from 'function/language';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';
import { RETURN_YN } from 'types/type';

/**
 * 협업 게시판 > 신규 원료 코드 신청 - 코드 만들기
 */
const Create = ({ show, inform, isSetting, seq, onHide, onSetting }) => {
  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* state */
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);
  // 하위 분류 목록
  const [mtrilGrpCds, setMtrilGrpCds] = useState([]);
  // 신규 원료 코드 신청 수정 요청 데이터
  const [rawMaterialData, setRawMaterialData] = useObjReducer({});
  // 원료 코드 목록
  const [rawMaterials, setRawMaterials] = useState([]);
  // 신규/기존 구분 (0: 신규, 1: 기존)
  const [type, setType] = useState(0);

  /* watch */
  useEffect(() => {
    // 모달이 표시된 경우
    if (!show) {
      return;
    }

    // 데이터 초기화
    if (isSetting) {
      setRawMaterialData({
        rwmtrCd: '',
        rwmtrNm: inform.rwmtrNm,
        mtrilGrpCd: inform.mtrilGrpCd
      });
    } else {
      setRawMaterialData({
        rwmtrCd: '',
        rwmtrNm: '',
        mtrilGrpCd: ''
      });
    }

    setType(0);

    // 원료 코드 목록 조회 요청
    requestRawMaterials();

    // 하위 분류 목록 조회 요청
    requestMtrilGrpCd();
  }, [show]);
  useEffect(() => {
    // 기존 선택 시 원료 명 / 하위 분류 자동 입력
    if (type !== 1) {
      return;
    }

    const { rwmtrNm = '', mtrilGrpNm } =
      rawMaterials.find(
        ({ rwmtrCd } = {}) => rwmtrCd === rawMaterialData.rwmtrCd
      ) ?? {};

    setRawMaterialData({
      rwmtrNm,
      mtrilGrpCd:
        mtrilGrpCds.find(({ codeNmKr } = {}) => codeNmKr === mtrilGrpNm)
          ?.codeCd ?? ''
    });
  }, [rawMaterialData.rwmtrCd]);

  /* method */
  // 하위 분류 목록 조회 요청
  const requestMtrilGrpCd = async () => {
    const { list = [] } = await requestCode({ codeGroup: 'rwmtr_subclass' });

    setMtrilGrpCds(list);
  };
  // 원료 코드 목록 조회 요청
  const requestRawMaterials = async () => {
    const { list = [] } = await requestList({ useYn: RETURN_YN.Y });

    setRawMaterials(list);
  };

  /* event */
  // 설정
  const onSubmit = async () => {
    if (isDisableSetting) {
      return;
    }

    setIsDisableSetting(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (!isValidRequired(rawMaterialData)) {
        throw { code: RESPONSE_CODE.BadRequestUpdate };
      }

      if (!isSetting) {
        const { code, message } = await requestUpdate(seq, {
          ...inform,
          ...rawMaterialData
        });

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }

        const { code: confirmCode, message: confirmMessage } =
          await requestConfirm(seq);

        if (confirmCode !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: confirmMessage
          };
        }
      }

      // 설정 시 이벤트 실행
      onSetting(rawMaterialData);

      // 모달 숨김
      onHide();
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
      setIsDisableSetting(false);
    }
  };

  /* view */
  return (
    <Modal
      show={show}
      className={{ footer: 'align-items-center justify-content-center' }}
      size="xl"
      onHide={onHide}
    >
      <Fragment key="header">{getText('createRawMaterial')}</Fragment>
      <Fragment key="body">
        <Flex>
          {new Array(2).fill(0).map((value, index) => (
            <Form.Check
              key={`type${index}`}
              label={[getText('new'), getText('exist')][index]}
              type="radio"
              checked={type === index}
              className={classNames({ 'ms-2': index === 1 })}
              name="type"
              value={index}
              onChange={({ target: { value } }) => setType(Math.floor(value))}
            />
          ))}
        </Flex>
        <List
          columns={[
            `* ${getText('rwmtrCd')}`,
            `${type === 0 ? '* ' : ''}${getText('rwmtrNm')}`,
            `${type === 0 ? '* ' : ''}${getText('rawMaterialCtgryNmKr')}`
          ]}
          responsive={false}
        >
          <tr>
            <td className="align-middle">
              {type === 1 ? (
                // 기존
                <Search
                  datas={array2ValueAndText(rawMaterials, 'rwmtrCd')}
                  value={rawMaterialData.rwmtrCd}
                  setValue={rwmtrCd => setRawMaterialData({ rwmtrCd })}
                  onKeyUp={event => {
                    onEnterKeyPress(event, onSubmit);
                  }}
                />
              ) : (
                // 신규
                <Form.Control
                  size="sm"
                  type="text"
                  value={rawMaterialData.rwmtrCd}
                  autoFocus
                  maxLength={8}
                  name="rwmtrCd"
                  onChange={event => onInputChange(event, setRawMaterialData)}
                  onKeyUp={event => onEnterKeyPress(event, onSubmit)}
                />
              )}
            </td>
            <td className="align-middle">
              <Form.Control
                readOnly={type === 1}
                size="sm"
                type="text"
                value={rawMaterialData.rwmtrNm}
                maxLength={30}
                name="rwmtrNm"
                onChange={event => onInputChange(event, setRawMaterialData)}
                onKeyUp={event => onEnterKeyPress(event, onSubmit)}
              />
            </td>
            <td>
              <Table bordered className="fs--1 mb-0 text-start">
                <tbody>
                  <tr>
                    <th className="align-middle py-1">
                      {getText('rawMaterialCtgryCd')}
                    </th>
                    <td className="py-1">
                      <Form.Control
                        readOnly
                        size="sm"
                        type="text"
                        value={rawMaterialData.mtrilGrpCd}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th className="align-middle py-1">Item</th>
                    <td className="py-1">
                      {type === 1 ? (
                        // 기존
                        <Form.Control
                          readOnly
                          size="sm"
                          type="text"
                          value={
                            mtrilGrpCds.find(
                              ({ codeCd } = {}) =>
                                codeCd === rawMaterialData.mtrilGrpCd
                            )?.codeNmKr ?? ''
                          }
                        />
                      ) : (
                        // 신규
                        <Select
                          datas={array2ValueAndText(
                            mtrilGrpCds,
                            'codeCd',
                            'codeNmKr'
                          )}
                          placeholder="Item"
                          value={rawMaterialData.mtrilGrpCd}
                          name="mtrilGrpCd"
                          onChange={event => {
                            onInputChange(event, setRawMaterialData);
                          }}
                        />
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </td>
          </tr>
        </List>
      </Fragment>
      <Fragment key="footer">
        <Button variant="secondary" onClick={onHide}>
          {getText('cancel')}
        </Button>
        <Button disabled={isDisableSetting} className="ms-2" onClick={onSubmit}>
          {getText('ok')}
        </Button>
      </Fragment>
    </Modal>
  );
};

Create.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 신규 원료 코드 신청 정보
  inform: PropTypes.object,

  // 신청/수정 화면 여부
  isSetting: PropTypes.bool,

  // 신규 원료 코드 신청 번호
  seq: PropTypes.number,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func,

  // 설정 시 이벤트
  onSetting: PropTypes.func
};
Create.defaultProps = {
  show: false,
  isSetting: false,
  inform: {}
};

export default Create;
